// PostHog client initialization. Next.js runs instrumentation-client before
// hydration and before any component renders, so PostHog is guaranteed ready
// before any client-side capture (e.g. CaptureOnMount firing on mount).
// This is the documented Next.js setup; it replaces the previous module-scope
// init inside posthog-client.tsx.
import { datadogRum } from "@datadog/browser-rum";
import {
  nextjsPlugin,
  onRouterTransitionStart,
} from "@datadog/browser-rum-nextjs";
import { getEnvironment } from "@repo/common";
import { env } from "@repo/env";
import { initBotId } from "botid/client/core";
import posthog from "posthog-js";

import { shouldSendPostHogEvent } from "@/lib/analytics/posthog-event-filters";
import { shouldSendRumEvent } from "@/lib/analytics/rum-error-filters";
import {
  captureEmailChangeLinkToken,
  EMAIL_CHANGE_PATH,
} from "@/lib/email-change-link";
import { startFingerprint } from "@/lib/fingerprint-client";
import { RISK_EVENT_ROUTES } from "@/lib/risk-event-routes";
import { rememberBrowserTimezone } from "@/lib/risk-signal-cookies";

// Next.js calls this on App Router client navigations; the Datadog RUM
// nextjsPlugin uses it to mark transitions. Must be a top-level export of
// instrumentation-client for Next.js to pick it up.
export { onRouterTransitionStart };

// Before any SDK below reads `location.href`: an email-change link carries a
// sign-in token in its fragment, and PostHog records the URL at init.
captureEmailChangeLinkToken();

// Shadow-mode risk events. BotID patches fetch first so Fingerprint's wrapper
// runs outermost: its event id header is set before BotID copies the headers.
initBotId({ protect: RISK_EVENT_ROUTES });
startFingerprint();
rememberBrowserTimezone();

// Remote replay settings must never enable capture of the confirmation POST body.
if (
  env.NEXT_PUBLIC_POSTHOG_KEY &&
  window.location.pathname !== EMAIL_CHANGE_PATH
) {
  posthog.init(env.NEXT_PUBLIC_POSTHOG_KEY, {
    // Ad blockers maintain lists of known analytics domains and block requests to them.
    // A reverse proxy bypasses this.
    // See https://posthog.com/docs/advanced/proxy
    api_host: "/t",
    ui_host: "https://us.posthog.com",
    // Inject the remote-config script into <head> instead of before the first
    // `body > script`. The default placement made PostHog's own
    // `/t/array/<token>/config.js` tag land on top of the first script React had
    // server-rendered into <body>, so hydration found a different element than
    // the server sent and threw React #418: the single highest-volume error in
    // the project. React #418 is recoverable, so it also discarded the SSR
    // output and re-rendered those pages client-side.
    external_scripts_inject_target: "head",
    // Track everyone, including anonymous visitors. Cold-outbound clickers
    // bounce on the auth screen before signing up; without anon profiles
    // they'd get no session replay. The 5s minimum recording duration set
    // in PostHog project settings filters out link-prefetcher / bot noise.
    person_profiles: "always",
    capture_pageview: true,
    // Disable console log recording to prevent rrweb serialization errors
    // when logging complex objects (TypeError: eM.map is not a function)
    enable_recording_console_log: false,
    // Session replay is switched on for the whole project from PostHog's remote
    // config, and remote config has no notion of where the page is served from,
    // so every `bun run dev` and every headless dev-test run lands in the same
    // replay list as production traffic. `disable_session_recording` is ANDed
    // with the remote setting rather than overriding it, so `false` outside dev
    // leaves that switch in charge.
    disable_session_recording: getEnvironment() === "dev",
    // Both sub-options ship OFF unless `defaults` is set, which it is not.
    // `ignore_text_selection` stops a click-and-drag text selection from being
    // read as three angry clicks, and `content_ignorelist` covers the built-in
    // pagination words ("next", "prev", ...) where repeat clicking is normal
    // use, not frustration.
    rageclick: { ignore_text_selection: true, content_ignorelist: true },
    // `false` keeps exception-autocapture's script, listeners and stack parsing
    // out of local development entirely; `undefined` leaves the decision to
    // PostHog's remote config, which is how it must stay everywhere else: any
    // literal here, including an object, overrides the remote kill switch.
    capture_exceptions: getEnvironment() === "dev" ? false : undefined,
    // posthog-js drops every capture from a browser it reads as automated, and
    // says nothing: the SDK reports itself loaded, opted in and configured, and
    // `capture()` returns normally while no request is ever made. A headless
    // Playwright browser matches, so no analytics change could be verified
    // locally, which is how a client and a server event ended up naming the
    // same join key two different things without anyone noticing.
    //
    // Dev only. In production the filter is the thing keeping crawler traffic
    // out of the numbers this experiment is read from.
    opt_out_useragent_filter: getEnvironment() === "dev",
    // Drops known-benign third-party exceptions. Shares its pattern list with
    // Datadog RUM so the two cannot drift.
    before_send: shouldSendPostHogEvent,
    on_request_error: (response) => {
      console.warn("[PostHog] Request failed:", response.statusCode);
    },
  });

  // Reaching for PostHog from the console is how the bot filter above was
  // finally found, after the SDK reported itself loaded, opted in and
  // configured while dropping every event.
  if (getEnvironment() === "dev") {
    (window as unknown as { __ph: typeof posthog }).__ph = posthog;
  }
}

// Datadog RUM. The nextjsPlugin sets `trackViewsManually`, so init alone starts
// NO session and sends nothing. A session begins only once a view is started,
// which `<DatadogRumViews />` does: mounted per route group, and per-group
// sampling lives in lib/analytics/rum-session-sampling.ts. `sessionSampleRate`
// below must therefore stay at 100: it is a single number for every route, so
// lowering it would sample the whole application instead of one group. Init must
// run here at module load (before React renders) so the SDK and plugin are ready
// before the first view starts. Stays inert without app id + token.
// A developer exercising RUM against a dev server has to ask for it. Nothing
// else can turn a dev host on, so a branch that predates this flag stays silent
// no matter which hostname it runs under.
const rumDevOptIn =
  getEnvironment() === "dev" && env.NEXT_PUBLIC_DD_RUM_DEV === "1";

if (
  env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID &&
  env.NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN &&
  // Don't collect RUM from local development. Local sessions (including
  // headless dev-test / Playwright runs on *.localhost) otherwise land in the
  // same RUM application as production, skewing p75s and burning session
  // billing for zero signal. Mirrors instrumentation.node.ts, which likewise
  // skips OTel when getEnvironment() === "dev". Preview ("staging") keeps RUM.
  (getEnvironment() !== "dev" || rumDevOptIn)
) {
  datadogRum.init({
    applicationId: env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID,
    clientToken: env.NEXT_PUBLIC_DD_RUM_CLIENT_TOKEN,
    site: env.NEXT_PUBLIC_DD_RUM_SITE ?? "datadoghq.com",
    // Same service/env/version as the @vercel/otel backend so RUM correlates
    // with APM traces and reuses the terac-web client sourcemaps for error
    // symbolication (datadog-ci upload uses --service=terac-web).
    service: "terac-web",
    // `dev-local`, not `dev`: the RUM retention filters drop `env:dev`
    // post-ingestion, so a session tagged `dev` never reaches the Session
    // Explorer and the opt-in above would show the developer nothing. The
    // separate tag also keeps these sessions excludable from any analysis.
    env: rumDevOptIn ? "dev-local" : getEnvironment(),
    ...(env.NEXT_PUBLIC_DD_RUM_VERSION
      ? { version: env.NEXT_PUBLIC_DD_RUM_VERSION }
      : {}),
    // Drop known non-actionable errors (Outlook SafeLink crawlers, Google One
    // Tap / FedCM rejections) so real errors aren't buried in Error Tracking.
    beforeSend: shouldSendRumEvent,
    sessionSampleRate: 100,
    // Session replay stays with PostHog; no duplicate recordings or replay cost.
    sessionReplaySampleRate: 0,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: "mask-user-input",
    // 100% of backend traces linked; independent of session sampling, adds no
    // session cost (only governs trace-header propagation).
    traceSampleRate: 100,
    plugins: [nextjsPlugin()],
    // Inject the W3C `traceparent` header only into our own same-origin /api/*
    // requests (tRPC) so each resource stitches into the @vercel/otel backend
    // trace. Same-origin means no CORS preflight, and PostHog's /t reverse
    // proxy plus all third-party hosts are excluded.
    allowedTracingUrls: [
      {
        match: (url: string) => {
          try {
            const parsed = new URL(url, window.location.origin);
            return (
              parsed.origin === window.location.origin &&
              parsed.pathname.startsWith("/api/")
            );
          } catch {
            return false;
          }
        },
        propagatorTypes: ["tracecontext"],
      },
    ],
  });
}
